export interface HrApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: unknown;
}

export interface HrBranch {
  id: string;
  companyId: string;
  companyName?: string;
  sourceClientId?: string | null;
  sourceWarehouseId?: string | null;
  branchCode: string;
  branchName: string;
  branchType: string;
  branchSlug?: string | null;
  isPrimary: boolean;
  email?: string | null;
  phoneNumber?: string | null;
  countryId: string;
  countryName?: string;
  cityId: string;
  cityName?: string;
  districtId: string;
  districtName?: string;
  fullAddress: string;
  shortAddress?: string | null;
  zipCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  managerUserId?: string | null;
  syncStatus?: string;
  lastSyncedAt?: string | null;
  status: string;
  createdAt: string;
}

export interface HrBranchPayload {
  companyId: string;
  sourceClientId?: string | null;
  sourceWarehouseId?: string | null;
  branchCode: string;
  branchName: string;
  branchType: string;
  branchSlug?: string | null;
  isPrimary?: boolean;
  email?: string | null;
  phoneNumber?: string | null;
  countryId: string;
  cityId: string;
  districtId: string;
  fullAddress: string;
  shortAddress?: string | null;
  zipCode?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  managerUserId?: string | null;
  syncStatus?: string;
  status?: string;
}

export interface HrLocationOption {
  id: string;
  name: string;
}

const hrApiBaseUrl =
  process.env.REACT_APP_HR_API_BASE_URL || process.env.REACT_APP_BASE_URL || "";

function getAuthTokenFromCookies(): string | null {
  if (typeof document === "undefined") return null;

  const cookieEntries = document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("="));

  const cookies = cookieEntries.reduce<Record<string, string>>(
    (acc, [key, ...value]) => {
      if (!key) return acc;
      acc[key] = value.join("=");
      return acc;
    },
    {},
  );

  return (
    cookies["auth"] ||
    cookies["customer_auth"] ||
    cookies["customer-auth"] ||
    cookies["jwt"] ||
    cookies["token"] ||
    cookies["dev-super-auth"] ||
    cookies["dev-auth-token"] ||
    null
  );
}

function getAuthTokenFromLocalStorage(): string | null {
  if (typeof window === "undefined") return null;

  return (
    window.localStorage.getItem("auth") ||
    window.localStorage.getItem("customer_auth") ||
    window.localStorage.getItem("customer-auth") ||
    window.localStorage.getItem("jwt") ||
    window.localStorage.getItem("token") ||
    window.localStorage.getItem("accessToken") ||
    window.localStorage.getItem("authToken") ||
    window.localStorage.getItem("dev-super-auth") ||
    window.localStorage.getItem("dev-auth-token") ||
    null
  );
}

function getAuthToken(): string | null {
  return getAuthTokenFromCookies() || getAuthTokenFromLocalStorage();
}

async function hrRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<HrApiResponse<T>> {
  if (!hrApiBaseUrl) {
    throw new Error("REACT_APP_HR_API_BASE_URL is not configured");
  }

  const token = getAuthToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (!(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${hrApiBaseUrl}${path}`, {
    credentials: "include",
    mode: "cors",
    headers,
    ...init,
  });

  if (!response.ok) {
    let errMessage = `HR API request failed: ${response.status} ${response.statusText}`;
    try {
      const errBody = await response.json() as any;
      if (errBody?.message) errMessage = errBody.message;
      else if (errBody?.error) errMessage = errBody.error;
    } catch {
      // ignore JSON parse error, use status text
    }
    throw new Error(errMessage);
  }

  const data = (await response.json()) as HrApiResponse<T>;
  return data;
}

export async function fetchHrCompany() {
  return hrRequest<any>("/company");
}

export async function updateHrCompany(id: string, payload: any) {
  return hrRequest<any>(`/company/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function fetchHrBranches(page = 1, limit = 50, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<{
    branches: any[];
    total: number;
    page: number;
    totalPages: number;
  }>(`/company-branch${query}`);
}

export async function fetchHrEmployees(page = 1, limit = 100, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<{
    employees: any[];
    total: number;
    page: number;
    totalPages: number;
  }>(`/employee${query}`);
}

export async function createHrEmployee(payload: any) {
  return hrRequest<any>(`/employee`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrEmployee(id: string, payload: any) {
  const isFormData = payload instanceof FormData;
  return hrRequest<any>(`/employee/${id}`, {
    method: "PATCH",
    body: isFormData ? payload : JSON.stringify(payload),
  });
}

export async function fetchHrEmployeeById(id: string) {
  return hrRequest<any>(`/employee/${id}`);
}

export async function deleteHrEmployee(id: string) {
  return hrRequest<null>(`/employee/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrBranchById(id: string) {
  return hrRequest<HrBranch>(`/company-branch/${id}`);
}

export async function createHrBranch(payload: HrBranchPayload) {
  return hrRequest<HrBranch>(`/company-branch`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrBranch(
  id: string,
  payload: Partial<HrBranchPayload>,
) {
  return hrRequest<HrBranch>(`/company-branch/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrBranch(id: string) {
  return hrRequest<null>(`/company-branch/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrCountries(searchFor = "") {
  const query = `?page=1&limit=50${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<{ countries: HrLocationOption[] }>(
    `/location/country${query}`,
  );
}

export async function fetchHrCities(countryId: string, searchFor = "") {
  const filter = JSON.stringify({ countryId });
  const query = `?page=1&limit=50&filter=${encodeURIComponent(filter)}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<{ cities: HrLocationOption[] }>(`/location/city${query}`);
}

export async function fetchHrDistricts(cityId: string, searchFor = "") {
  const filter = JSON.stringify({ cityId });
  const query = `?page=1&limit=50&filter=${encodeURIComponent(filter)}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<{ districts: HrLocationOption[] }>(
    `/location/district${query}`,
  );
}

/* =========================================================
   NOTICEBOARD APIs
========================================================= */

export interface HrNoticeboard {
  id: string;
  companyId: string;
  title: string;
  content: string;
  target: string;
  isPinned: boolean;
  publisherName: string;
  attachmentUrl: string | null;
  publishedAt: string;
  createdAt: string;
}

export interface HrNoticeboardPayload {
  companyId: string;
  title: string;
  content: string;
  target?: string;
  isPinned?: boolean;
  publisherName: string;
  attachmentUrl?: string | null;
  publishedAt?: string;
}

export async function fetchNoticeboards(page = 1, limit = 50, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<{
    noticeboards: HrNoticeboard[];
    total: number;
    page: number;
    totalPages: number;
  }>(`/noticeboard${query}`);
}

export async function createNoticeboard(payload: HrNoticeboardPayload) {
  return hrRequest<HrNoticeboard>(`/noticeboard`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateNoticeboard(
  id: string,
  payload: Partial<HrNoticeboardPayload>,
) {
  return hrRequest<HrNoticeboard>(`/noticeboard/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteNoticeboard(id: string) {
  return hrRequest<null>(`/noticeboard/${id}`, {
    method: "DELETE",
  });
}

/* =========================================================
   DEPARTMENT APIs
========================================================= */

export interface HrDepartment {
  id: string;
  companyId: string;
  branchId: string;
  deptId: string;
  parentDepartmentId: string | null;
  departmentName: string;
  hodId: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface HrDepartmentPayload {
  parentDepartmentId?: string | null;
  departmentName: string;
  hodId?: string | null;
  status?: string;
}

export async function fetchHrDepartments(page = 1, limit = 50, searchFor = "", filter = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;
  return hrRequest<{
    departments: HrDepartment[];
    total: number;
    page: number;
    totalPages: number;
  }>(`/department${query}`);
}

export async function createHrDepartment(payload: HrDepartmentPayload) {
  return hrRequest<HrDepartment>(`/department`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrDepartment(
  id: string,
  payload: Partial<HrDepartmentPayload>,
) {
  return hrRequest<HrDepartment>(`/department/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrDepartment(id: string) {
  return hrRequest<null>(`/department/${id}`, {
    method: "DELETE",
  });
}

/* =========================================================
   DESIGNATION APIs
========================================================= */

export interface HrDesignation {
  id: string;
  companyId: string;
  branchId: string;
  designationId: string; // formatted code like DES-00001
  parentDesignationId: string | null;
  designationName: string;
  departmentId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface HrDesignationPayload {
  branchId: string;
  parentDesignationId?: string | null;
  designationName: string;
  departmentId: string;
  status?: string;
}

export async function fetchHrDesignations(page = 1, limit = 50, searchFor = "", filter = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;
  return hrRequest<{
    designations: HrDesignation[];
    total: number;
    page: number;
    totalPages: number;
  }>(`/designation${query}`);
}

export async function createHrDesignation(payload: HrDesignationPayload) {
  return hrRequest<HrDesignation>(`/designation`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrDesignation(
  id: string,
  payload: Partial<HrDesignationPayload>,
) {
  return hrRequest<HrDesignation>(`/designation/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrDesignation(id: string) {
  return hrRequest<null>(`/designation/${id}`, {
    method: "DELETE",
  });
}

// =========================================================
// RBAC API HELPERS
// =========================================================

export interface HrRole {
  id: string;
  companyId: string;
  roleName: string;
  roleCode: string;
  description: string;
  isSystemRole: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  totalEmployees: number;
  totalPermissions: number;
}

export interface HrBranchModuleConfig {
  module: string;
  enabled: boolean;
}

export interface HrPermission {
  id: string;
  module: string;
  action: string;
  permissionKey: string;
  description: string;
}

export interface HrRolePermissionMapping {
  id: string;
  roleId: string;
  permissionId: string;
  dataScope: string;
}

export async function fetchHrRoles(companyId: string, branchId: string) {
  return hrRequest<HrRole[]>(`/rbac/branch-role?companyId=${companyId}&branchId=${branchId}`);
}

export async function fetchHrBranchConfig(companyId: string, branchId: string) {
  return hrRequest<HrBranchModuleConfig[]>(`/rbac/branch-config?companyId=${companyId}&branchId=${branchId}`);
}

export async function syncHrBranchConfig(companyId: string, branchId: string, enabledModules: string[]) {
  return hrRequest<any>(`/rbac/sync`, {
    method: "POST",
    body: JSON.stringify({ companyId, branchId, enabledModules }),
  });
}

export async function fetchHrGroupedPermissions() {
  return hrRequest<Record<string, HrPermission[]>>(`/rbac/grouped`);
}

export async function fetchHrRolePermissions(roleId: string) {
  return hrRequest<HrRolePermissionMapping[]>(`/rbac/role/${roleId}/permissions`);
}

export async function updateHrRolePermissions(roleId: string, permissions: { permissionId: string; dataScope: string }[]) {
  return hrRequest<any>(`/rbac/role/${roleId}/permissions`, {
    method: "PATCH",
    body: JSON.stringify({ permissions }),
  });
}

export async function createHrRole(payload: { companyId: string; roleName: string; roleCode: string; description?: string; status?: string }) {
  return hrRequest<HrRole>(`/rbac/role`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrRole(roleId: string, payload: { roleName: string; description?: string; status?: string }) {
  return hrRequest<HrRole>(`/rbac/role/${roleId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrRole(roleId: string) {
  return hrRequest<any>(`/rbac/role/${roleId}`, {
    method: "DELETE",
  });
}

export async function fetchHrLeaveRequests(page = 1, limit = 10, filter = "") {
  let query = `?page=${page}&limit=${limit}`;
  if (filter) {
    query += `&filter=${encodeURIComponent(filter)}`;
  }
  return hrRequest<any>(`/leave/request${query}`);
}

export async function updateHrLeaveStatus(
  id: string,
  status: "APPROVED" | "REJECTED" | "PENDING",
  rejectionReason?: string,
  leaveDateIds?: string[]
) {
  const body: any = { status };
  if (rejectionReason) {
    body.rejectionReason = rejectionReason;
  }
  if (leaveDateIds) {
    body.leaveDateIds = leaveDateIds;
  }
  return hrRequest<any>(`/leave/request/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function createHrLeaveRequest(payload: any) {
  const isFormData = payload instanceof FormData;
  return hrRequest<any>(`/leave/request`, {
    method: "POST",
    body: isFormData ? payload : JSON.stringify(payload),
  });
}

export async function updateHrLeaveRequest(id: string, payload: any) {
  const isFormData = payload instanceof FormData;
  return hrRequest<any>(`/leave/request/${id}`, {
    method: "PUT",
    body: isFormData ? payload : JSON.stringify(payload),
  });
}

export async function deleteHrLeaveRequest(id: string) {
  return hrRequest<any>(`/leave/request/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrLeaveTypes(page = 1, limit = 50, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/leave/type${query}`);
}

export async function createHrLeaveType(payload: any) {
  return hrRequest<any>(`/leave/type`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrLeaveType(id: string, payload: any) {
  return hrRequest<any>(`/leave/type/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrLeaveType(id: string) {
  return hrRequest<any>(`/leave/type/${id}`, {
    method: "DELETE",
  });
}

// ── Leave Report CRUD ────────────────────────────────────────
export async function fetchHrLeaveReports(page = 1, limit = 10, employeeId = "") {
  let query = `?page=${page}&limit=${limit}`;
  if (employeeId) {
    const filter = JSON.stringify([{ field: "employeeId", value: employeeId }]);
    query += `&filter=${encodeURIComponent(filter)}`;
  }
  return hrRequest<any>(`/leave/report${query}`);
}

export async function createHrLeaveReport(payload: any) {
  return hrRequest<any>(`/leave/report`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrLeaveReport(id: string, payload: any) {
  return hrRequest<any>(`/leave/request/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrLeaveReport(id: string) {
  return hrRequest<any>(`/leave/request/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrWorkingFrom(page = 1, limit = 10, searchFor = "", filter = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;
  return hrRequest<any>(`/working-from${query}`);
}

export async function createHrWorkingFrom(payload: any) {
  return hrRequest<any>(`/working-from`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrWorkingFrom(id: string, payload: any) {
  return hrRequest<any>(`/working-from/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrWorkingFrom(id: string) {
  return hrRequest<any>(`/working-from/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrAttendanceBreaks(page = 1, limit = 50, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/attendance-break${query}`);
}

export async function fetchHrShiftTypes(page = 1, limit = 10, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/employee-shift/type${query}`);
}

export async function createHrShiftType(payload: any) {
  return hrRequest<any>(`/employee-shift/type`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrShiftType(id: string, payload: any) {
  return hrRequest<any>(`/employee-shift/type/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrShiftType(id: string) {
  return hrRequest<any>(`/employee-shift/type/${id}`, {
    method: "DELETE",
  });
}

export async function createHrAttendanceBreak(payload: any) {
  return hrRequest<any>(`/attendance-break`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrAttendanceBreak(id: string, payload: any) {
  return hrRequest<any>(`/attendance-break/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrAttendanceBreak(id: string) {
  return hrRequest<any>(`/attendance-break/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrFines(page = 1, limit = 10, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/settings/actions/fine${query}`);
}

export async function createHrFine(payload: any) {
  return hrRequest<any>(`/settings/actions/fine`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrFine(id: string, payload: any) {
  return hrRequest<any>(`/settings/actions/fine/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrFine(id: string) {
  return hrRequest<any>(`/settings/actions/fine/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrAwards(page = 1, limit = 10, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/award${query}`);
}

export async function createHrAward(payload: any) {
  return hrRequest<any>(`/award`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrAward(id: string, payload: any) {
  return hrRequest<any>(`/award/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrAward(id: string) {
  return hrRequest<any>(`/award/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrUniqueCodeSettings() {
  return hrRequest<any>(`/hr-unique-code-settings?page=1&limit=50`);
}

export async function updateHrUniqueCodeSettings(payload: { entity_type: string; series_code: string; connector: string; sequence_length: number }[]) {
  return hrRequest<any>(`/hr-unique-code-settings`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchAssetUniqueCodeSettings() {
  return hrRequest<any>(`/asset/settings/unique-code`);
}

export async function updateAssetUniqueCodeSettings(payload: { type: string; seriesCode: string; connector: string; sequenceLength: number }[]) {
  return hrRequest<any>(`/asset/settings/unique-code`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function fetchAssetTypes(page = 1, limit = 100, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/asset/settings/asset-type${query}`);
}

export async function createAssetType(payload: { name: string; isConstructionAssetType: boolean }) {
  return hrRequest<any>(`/asset/settings/asset-type`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAssetType(id: string, payload: { name: string; isConstructionAssetType: boolean }) {
  return hrRequest<any>(`/asset/settings/asset-type/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteAssetType(id: string) {
  return hrRequest<any>(`/asset/settings/asset-type/${id}`, {
    method: "DELETE",
  });
}



export async function fetchHrAttendanceSettings() {
  return hrRequest<any>(`/attendance/settings`);
}

export async function updateHrAttendanceSettings(payload: any) {
  return hrRequest<any>(`/attendance/settings`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchHrLoanAdvanceSettings() {
  return hrRequest<any>(`/loan-advance-settings`);
}

export async function updateHrLoanAdvanceSettings(payload: any) {
  return hrRequest<any>(`/loan-advance-settings`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchHrLoanAdvances(params?: any) {
  let query = "";
  if (params) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append("page", String(params.page));
    if (params.limit) searchParams.append("limit", String(params.limit));
    if (params.searchFor) searchParams.append("searchFor", params.searchFor);
    if (params.filter) searchParams.append("filter", typeof params.filter === "string" ? params.filter : JSON.stringify(params.filter));
    query = `?${searchParams.toString()}`;
  }
  return hrRequest<any>(`/loan-advance${query}`);
}

export async function fetchHrLoanAdvanceById(id: string) {
  return hrRequest<any>(`/loan-advance/${id}`);
}

export async function createHrLoanAdvance(payload: any) {
  return hrRequest<any>(`/loan-advance`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrLoanAdvance(id: string, payload: any) {
  return hrRequest<any>(`/loan-advance/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrLoanAdvance(id: string) {
  return hrRequest<any>(`/loan-advance/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrAppraisals(filters: any = {}, page = 1, limit = 50, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filters ? `&filter=${encodeURIComponent(JSON.stringify(filters))}` : ""}`;
  return hrRequest<any>(`/actions/appraisal${query}`);
}

export async function fetchHrAppraisalById(id: string) {
  return hrRequest<any>(`/actions/appraisal/${id}`);
}

export async function createHrAppraisal(payload: any) {
  return hrRequest<any>(`/actions/appraisal`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrAppraisal(id: string, payload: any) {
  return hrRequest<any>(`/actions/appraisal/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrAppraisal(id: string) {
  return hrRequest<any>(`/actions/appraisal/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrRelegations(page = 1, limit = 50, searchFor = "", filters: any = {}) {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filters && Object.keys(filters).length > 0 ? `&filter=${encodeURIComponent(JSON.stringify(filters))}` : ""}`;
  return hrRequest<any>(`/actions/relegation${query}`);
}

export async function fetchHrRelegationById(id: string) {
  return hrRequest<any>(`/actions/relegation/${id}`);
}

export async function createHrRelegation(payload: any) {
  return hrRequest<any>(`/actions/relegation`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrRelegation(id: string, payload: any) {
  return hrRequest<any>(`/actions/relegation/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrRelegation(id: string) {
  return hrRequest<any>(`/actions/relegation/${id}`, {
    method: "DELETE",
  });
}

export async function fetchHrWarningSettings() {
  return hrRequest<any>(`/settings/actions/warning`);
}

export async function saveHrWarningSettings(payload: { warningCount: number }) {
  return hrRequest<any>(`/settings/actions/warning`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchHrNoticePeriodSettings() {
  return hrRequest<any>(`/settings/actions/notice-period`);
}

export async function saveHrNoticePeriodSettings(payload: { noticePeriod: number; noticeTenure: string }) {
  return hrRequest<any>(`/settings/actions/notice-period`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchHrAttendance(month?: number, year?: number, departmentId?: string, page = 1, limit = 2000, searchFor = "") {
  const filters: {field: string; value: string}[] = [];
  if (month) filters.push({ field: "month", value: String(month) });
  if (year) filters.push({ field: "year", value: String(year) });
  if (departmentId) filters.push({ field: "departmentId", value: departmentId });
  let query = `?page=${page}&limit=${limit}`;
  if (searchFor) query += `&searchFor=${encodeURIComponent(searchFor)}`;
  if (filters.length) query += `&filter=${encodeURIComponent(JSON.stringify(filters))}`;
  return hrRequest<any>(`/attendance${query}`);
}

export async function fetchHrAttendanceCalendar(month: number, year: number, page = 1, limit = 50, searchFor = "", employeeId = "", departmentId = "") {
  let query = `?month=${month}&year=${year}&page=${page}&limit=${limit}`;
  if (searchFor) query += `&searchFor=${encodeURIComponent(searchFor)}`;
  if (employeeId) query += `&employeeId=${encodeURIComponent(employeeId)}`;
  if (departmentId) query += `&departmentId=${encodeURIComponent(departmentId)}`;
  return hrRequest<any>(`/attendance/calendar${query}`);
}

export async function markHrAttendanceManually(payload: any) {
  return hrRequest<any>(`/attendance`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrAttendance(id: string, payload: any = { status: "ABSENT" }) {
  return hrRequest<any>(`/attendance/${id}`, {
    method: "DELETE",
    body: JSON.stringify(payload),
  });
}

// ── Expense Request APIs ───────────────────────────────────────────────────
export async function fetchHrExpenseRequests(page = 1, limit = 10, searchFor = "") {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/expense-request${query}`);
}

export async function fetchHrExpenseRequestById(id: string) {
  return hrRequest<any>(`/expense-request/${id}`);
}

export async function updateHrExpenseRequestStatus(id: string, status: string) {
  return hrRequest<any>(`/expense-request/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

// ── Holiday APIs ───────────────────────────────────────────────────────────
export interface HrHolidayPayload {
  actionType?: "CREATE" | "DELETE";
  mode?: "SINGLE" | "BULK" | "SELECTED_DAYS";
  applyTo?: "EMPLOYEE" | "DEPARTMENT" | "BRANCH" | "COMPANY";
  title: string;
  description?: string;
  holidayDate?: string; // YYYY-MM-DD
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  holidayType?: "COMPANY" | "NATIONAL";
  selectedDays?: string[];
  branchIds?: string[];
  departmentIds?: string[];
  employeeIds?: string[];
}

export async function createHrHoliday(payload: HrHolidayPayload) {
  return hrRequest<any>("/holiday", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrHoliday(id: string, payload: HrHolidayPayload) {
  return hrRequest<any>(`/holiday/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchHrHolidayCalendar(month: number, year: number, employeeId?: string) {
  const query = `?month=${month}&year=${year}${employeeId ? `&employeeId=${employeeId}` : ""}`;
  return hrRequest<any>(`/holiday/calendar${query}`);
}

export async function fetchHrUpcomingHolidays(page = 1, limit = 10) {
  return hrRequest<any>(`/holiday/upcoming?page=${page}&limit=${limit}`);
}

export async function deleteHrHoliday(idOrPayload: string | any) {
  if (typeof idOrPayload === "string") {
    return hrRequest<any>(`/holiday/${idOrPayload}`, {
      method: "DELETE",
    });
  } else {
    return hrRequest<any>(`/holiday`, {
      method: "DELETE",
      body: JSON.stringify(idOrPayload),
    });
  }
}

export async function fetchHrAppreciations(page = 1, limit = 10, searchFor = "", companyId = "") {
  let filter = "";
  if (companyId) {
    filter = `&filter=${encodeURIComponent(JSON.stringify([{ field: "companyId", value: companyId }]))}`;
  }
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filter}`;
  return hrRequest<any>(`/appreciation${query}`);
}

export async function createHrAppreciation(payload: FormData) {
  return hrRequest<any>(`/appreciation`, {
    method: "POST",
    body: payload,
  });
}

export async function updateHrAppreciation(id: string, payload: FormData) {
  return hrRequest<any>(`/appreciation/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deleteHrAppreciation(id: string) {
  return hrRequest<any>(`/appreciation/${id}`, {
    method: "DELETE",
    body: JSON.stringify({ status: "INACTIVE" }),
  });
}

export async function fetchHrRewards(page = 1, limit = 10, searchFor = "", filters: any = {}) {
  const query = `?page=${page}&limit=${limit}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}${filters && Object.keys(filters).length > 0 ? `&filter=${encodeURIComponent(JSON.stringify(filters))}` : ""}`;
  return hrRequest<any>(`/actions/reward${query}`);
}

export async function createHrReward(payload: any) {
  return hrRequest<any>(`/actions/reward`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateHrReward(id: string, payload: any) {
  return hrRequest<any>(`/actions/reward/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrReward(id: string) {
  return hrRequest<any>(`/actions/reward/${id}`, {
    method: "DELETE",
  });
}

// ── Employee Document APIs ────────────────────────────────────────────────
export async function verifyHrEmployeeDocument(employeeId: string, docId: string, isVerified: boolean) {
  return hrRequest<any>(`/employee/${employeeId}/document/${docId}`, {
    method: "PATCH",
    body: JSON.stringify({ isVerified }),
  });
}

export async function deleteHrEmployeeDocument(employeeId: string, docId: string) {
  return hrRequest<any>(`/employee/${employeeId}/document/${docId}`, {
    method: "DELETE",
  });
}

// ── Employee Shift Roster APIs ────────────────────────────────────────────────
export async function fetchHrShiftRosters(page = 1, limit = 100, filter = "", searchFor = "") {
  const query = `?page=${page}&limit=${limit}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}${searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ""}`;
  return hrRequest<any>(`/employee-shift/roster${query}`);
}

export async function fetchHrShiftRosterById(id: string) {
  return hrRequest<any>(`/employee-shift/roster/${id}`);
}

export async function createHrShiftRoster(payload: FormData | any) {
  const isFormData = payload instanceof FormData;
  return hrRequest<any>(`/employee-shift/roster`, {
    method: "POST",
    body: isFormData ? payload : JSON.stringify(payload),
  });
}

export async function updateHrShiftRoster(id: string, payload: any) {
  return hrRequest<any>(`/employee-shift/roster/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteHrShiftRoster(id: string) {
  return hrRequest<any>(`/employee-shift/roster/${id}`, {
    method: "DELETE",
  });
}
